import { JSX, memo, useContext } from "react";
import { createPortal } from "react-dom";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { NavigationControl } from "react-map-gl/mapbox";
import { CompetitiveInsightsColorTheme } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsColorThemeContext";
import { CompetitiveInsightsMapContext } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapContext";
import { CompetitiveInsightsMapOverlapTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsMapOverlapTypeEnum";
import { getMapOverlapColor } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";
import { MapAppearanceControl } from "@app/shared/map-appearance/components/MapAppearanceControl";
import { RecenterControl } from "@app/shared/mapping/RecenterControl";

const CompetitiveInsightsMapOverlapLegend = (): JSX.Element => {
  const competitiveInsightsMapContext = useContext(CompetitiveInsightsMapContext);

  const { getColor } = CompetitiveInsightsColorTheme.useMapColorTheme();

  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const container = useMapboxControl({ mapRef: competitiveInsightsMapContext.mapRef, position: "top-right" });
  if (!container) return null;

  return createPortal(
    <div>
      <NavigationControl data-html2canvas-ignore="true" position="top-right" showCompass={false} />

      <RecenterControl
        ignoreInMapExport={true}
        imagePath={`${imagesDir}Recenter.png`}
        mapRef={competitiveInsightsMapContext.mapRef}
        onClick={competitiveInsightsMapContext.onMapLoad}
        position="top-right"
      />

      <MapAppearanceControl
        colorThemeContext={CompetitiveInsightsColorTheme.Context}
        colorThemeFlyoutTitle="Competitive Insights Map Color Theme"
        colorThemeSelectLabel="Color Theme"
        mapRef={competitiveInsightsMapContext.mapRef}
        position="bottom-right"
        testId="competitive-insights__map--color-theme-select"
      />

      {competitiveInsightsMapContext.mapOverlapTypes?.length > 0 && (
        <div id="competitive-insights__map--legend-container">
          <div
            id="competitive-insights__map--legend"
            className="col-12 p-2"
          >
            <div className="demibold">
              County Overlaps
            </div>
            {competitiveInsightsMapContext.mapOverlapTypes.map((mapOverlapType, index) => (
              <div className="d-flex align-items-center" key={index}>
                <QesIcon
                  className="me-2"
                  iconName={FontAwesome.Square}
                  style={{
                    color: getColor(getMapOverlapColor(mapOverlapType.name as CompetitiveInsightsMapOverlapTypeEnum)),
                    fill: getColor(getMapOverlapColor(mapOverlapType.name as CompetitiveInsightsMapOverlapTypeEnum))
                  }}
                  description={mapOverlapType.name}
                  width="24"
                />
                {mapOverlapType.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>,
    container
  )
}

export default memo(CompetitiveInsightsMapOverlapLegend);